import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Box,
  Alert,
  AlertTitle,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import { useGetPrivacyPolicyQuery } from "../../../Services/policy/Policy";

// Brand Orange
const BRAND_ORANGE = "#D9490C";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.spacing(2),
    width: "90vw",
    maxWidth: 800,
    maxHeight: "85vh",
    backgroundColor: theme.palette.background.paper,
    boxShadow: `0 8px 24px rgba(217, 73, 12, 0.3)`,
    fontFamily: "karbon-medium", // ✅ applied globally
    [theme.breakpoints.up("sm")]: {
      width: "600px",
    },
    [theme.breakpoints.up("md")]: {
      width: "700px",
    },
    [theme.breakpoints.up("lg")]: {
      width: "800px",
    },
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  paddingBottom: theme.spacing(1),
  background: `linear-gradient(135deg, ${BRAND_ORANGE}, #b33c0a)`,
  color: "#fff",
  margin: 0,
  fontSize: "1.25rem",
  fontFamily: "karbon-medium", // ✅ applied
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
    padding: theme.spacing(1),
  },
}));

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  fontFamily: "karbon-medium", // ✅ applied
  "& h1, & h2, & h3, & h4, & h5, & h6": {
    color: theme.palette.text.primary,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1.5),
    fontWeight: 700,
    lineHeight: 1.3,
  },
  "& p": {
    marginBottom: theme.spacing(2),
    lineHeight: 1.8,
    color: theme.palette.text.secondary,
    fontSize: "1rem",
    "&:empty": {
      display: "none",
    },
  },
  "& ul, & ol": {
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    color: theme.palette.text.secondary,
    fontSize: "1rem",
  },
  "& li": {
    marginBottom: theme.spacing(1),
    lineHeight: 1.6,
  },
  "& strong": {
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  "& a": {
    color: BRAND_ORANGE,
    textDecoration: "underline",
    cursor: "pointer",
    "&:hover": {
      color: theme.palette.primary.dark,
    },
  },
  fontSize: "1rem",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.9rem",
    padding: theme.spacing(1),
  },
}));

const PolicyModal = ({ closeModal, open = true }) => {
  const [content, setContent] = useState("");
  const location = useLocation();

  const {
    data: privacyPolicy,
    isLoading,
    error,
    refetch,
  } = useGetPrivacyPolicyQuery({});

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (privacyPolicy?.data?.privacy_policy) {
      setContent(privacyPolicy.data?.privacy_policy || "");
    }
  }, [privacyPolicy]);

  const handleRefresh = () => refetch();
  const handleClose = () => closeModal();

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      scroll="paper"
      maxWidth={false}
      fullWidth={false}
      aria-labelledby="privacy-dialog-title"
    >
      {/* Header */}
      <StyledDialogTitle
        id="privacy-dialog-title"
        sx={{ fontSize: isSmallScreen ? "1rem" : "1.25rem" }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <DescriptionIcon fontSize={isSmallScreen ? "small" : "medium"} />
          <Typography
            variant="h6"
            component="div"
            fontWeight="bold"
            fontSize={isSmallScreen ? "1rem" : "1.25rem"}
            sx={{ fontFamily: "karbon-medium" }} // ✅ typography override
          >
            Privacy Policy
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: "inherit" }}
          size={isSmallScreen ? "small" : "medium"}
        >
          {location.pathname === "/customer/privacy-policy" ? (
            ""
          ) : (
            <CloseIcon fontSize={isSmallScreen ? "small" : "medium"} />
          )}
        </IconButton>
      </StyledDialogTitle>

      {/* Content */}
      <DialogContent
        dividers
        sx={{
          padding: 0,
          maxHeight: isSmallScreen ? "65vh" : "85vh",
          overflowY: "auto",
          fontFamily: "karbon-medium", // ✅ applied
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: BRAND_ORANGE,
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        {isLoading && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="200px"
            gap={2}
            px={2}
            sx={{ fontFamily: "karbon-medium" }}
          >
            <CircularProgress size={48} sx={{ color: BRAND_ORANGE }} />
            <Typography
              variant="body1"
              color="text.secondary"
              fontSize={isSmallScreen ? "0.9rem" : "1rem"}
              sx={{ fontFamily: "karbon-medium" }}
            >
              Loading privacy policy...
            </Typography>
          </Box>
        )}

        {error && (
          <Box p={3}>
            <Alert
              severity="error"
              action={
                <Button
                  sx={{ color: BRAND_ORANGE, fontFamily: "karbon-medium" }}
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                >
                  Retry
                </Button>
              }
              sx={{
                fontSize: isSmallScreen ? "0.85rem" : "1rem",
                fontFamily: "karbon-medium",
              }}
            >
              <AlertTitle>Error Loading Content</AlertTitle>
              Failed to load privacy policy. Please try again.
            </Alert>
          </Box>
        )}

        {content && !isLoading && !error && (
          <Box p={3}>
            <ContentBox>
              <div dangerouslySetInnerHTML={{ __html: content }} />
            </ContentBox>
          </Box>
        )}

        {!content && !isLoading && !error && privacyPolicy && (
          <Box p={3}>
            <Alert
              severity="info"
              sx={{
                fontSize: isSmallScreen ? "0.85rem" : "1rem",
                fontFamily: "karbon-medium",
              }}
            >
              <AlertTitle>No Content Available</AlertTitle>
              No privacy policy are available at the moment.
            </Alert>
          </Box>
        )}
      </DialogContent>
    </StyledDialog>
  );
};

export default PolicyModal;
